'use client';

import { ReactNode } from 'react';
import { useRequireAuth, useRequireAdmin } from '@/hooks/useAuth';
import { Loader2 } from 'lucide-react';

interface ProtectedRouteProps {
  children: ReactNode;
  fallback?: ReactNode;
}

/**
 * Loading spinner component
 */
function LoadingSpinner() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-900">
      <div className="text-center">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-400 mx-auto mb-4" />
        <p className="text-slate-400">Loading...</p>
      </div>
    </div>
  );
}

/**
 * Protected Route Wrapper
 * 
 * Wraps a page component to require authentication.
 * Automatically redirects to login if user is not authenticated.
 * Shows loading spinner while checking auth status.
 * 
 * Usage:
 * ```tsx
 * export default function SettingsPage() {
 *   return (
 *     <ProtectedRoute>
 *       <SettingsContent />
 *     </ProtectedRoute>
 *   );
 * }
 * ```
 */
export function ProtectedRoute({ children, fallback }: ProtectedRouteProps) {
  const { loading, isAuthenticated } = useRequireAuth();

  if (loading) {
    return fallback ?? <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    // useRequireAuth handles the redirect, show loading while redirecting
    return fallback ?? <LoadingSpinner />;
  }

  return <>{children}</>;
}

/**
 * Admin Route Wrapper
 * 
 * Wraps a page component to require admin access.
 * Redirects to login if not authenticated, or to dashboard if not admin.
 * 
 * Usage:
 * ```tsx
 * export default function AdminPage() {
 *   return (
 *     <AdminRoute>
 *       <AdminContent />
 *     </AdminRoute>
 *   );
 * }
 * ```
 */
export function AdminRoute({ children, fallback }: ProtectedRouteProps) {
  const { loading, isAdmin } = useRequireAdmin();

  if (loading) {
    return fallback ?? <LoadingSpinner />;
  }

  if (!isAdmin) {
    // useRequireAdmin handles the redirect
    return fallback ?? <LoadingSpinner />;
  }

  return <>{children}</>;
}

/**
 * Higher-order component for protected pages
 * 
 * Usage:
 * ```tsx
 * function SettingsPage() {
 *   return <div>Settings content</div>;
 * }
 * 
 * export default withAuth(SettingsPage);
 * ```
 */
export function withAuth<P extends object>(
  Component: React.ComponentType<P>
): React.FC<P> {
  return function ProtectedComponent(props: P) {
    return (
      <ProtectedRoute>
        <Component {...props} />
      </ProtectedRoute>
    );
  };
}

/**
 * Higher-order component for admin pages
 */
export function withAdmin<P extends object>(
  Component: React.ComponentType<P>
): React.FC<P> {
  return function AdminComponent(props: P) {
    return (
      <AdminRoute>
        <Component {...props} />
      </AdminRoute>
    );
  };
}
